import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Search, ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import SocialShareButtons from "../components/SocialShareButtons";
import { Input } from "../components/ui/input";

export default function FAQPage() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    axios.get(`${API}/faqs`)
      .then(res => setFaqs(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = ["all", ...new Set(faqs.map(f => f.category))];

  const filtered = faqs.filter(f => {
    const matchesCategory = activeCategory === "all" || f.category === activeCategory;
    const matchesSearch = !search || 
      f.question.toLowerCase().includes(search.toLowerCase()) || 
      f.answer.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      <SEO title="FAQ" path="/faq" description="Frequently asked questions about All WHUFC and West Ham United. Find answers about the club, membership, tickets and more." />
      <Navbar />

      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            FREQUENTLY ASKED QUESTIONS
          </h1>
          <p className="text-white/80">Everything you need to know about West Ham United</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search FAQs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12 text-base"
            data-testid="faq-search"
          />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="faq-category-filter">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${
                activeCategory === cat
                  ? "bg-[#7C2C3B] text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-[#7C2C3B]"
              }`}
              data-testid={`faq-filter-${cat}`}
            >
              {cat === "all" ? `All (${faqs.length})` : `${cat} (${faqs.filter(f => f.category === cat).length})`}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No FAQs found matching your search.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((faq) => (
              <div
                key={faq.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-shadow hover:shadow-sm"
                data-testid={`faq-item-${faq.id}`}
              >
                <button
                  onClick={() => toggleItem(faq.id)}
                  className="w-full flex items-center justify-between p-4 text-left"
                  data-testid={`faq-toggle-${faq.id}`}
                >
                  <div className="flex items-start gap-3 flex-1 pr-4">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#7C2C3B]/10 text-[#7C2C3B] mt-0.5 whitespace-nowrap">
                      {faq.category}
                    </span>
                    <span className="font-semibold text-[#0F172A] text-sm" style={{ fontFamily: 'Barlow Condensed' }}>
                      {faq.question}
                    </span>
                  </div>
                  {openItems[faq.id] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openItems[faq.id] && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="pl-[72px] text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                      {faq.answer}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-center text-sm text-slate-400">
          {filtered.length} of {faqs.length} FAQs shown
        </div>

        {/* Social Sharing */}
        <SocialShareButtons
          shareText="West Ham United FAQ - Everything you need to know about the Hammers!"
        />
      </div>

      <Footer />
    </div>
  );
}
